import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Program.css";
import {
  Container,
  SpaceBetween,
  Tabs,
  Box,
  SelectProps,
  Multiselect,
  MultiselectProps,
  Button,
  FormField,
  TokenGroup,
  Icon,
  Flashbar,
  FlashbarProps,
} from "@cloudscape-design/components";
import { getProgramOnboardRequest, getProgramMetricsRequest, getProgramDetailRequest } from "../../redux/actions/programActions";
import { RootState } from "../../redux/reducers/rootReducer";
import type { ProgramOnboardItem, ProgramMetricsData } from "../../redux/types/programTypes";
import {
  SingleStageCard, SingleSummaryCard, PhicStatusCard, MultiModeList,
  ProductionOnlyDetail, ShipmentOnlyDetail, TestingFPYDetail,
  MachineDowntimeDetail, LineUtilizationDetail, PHICStatusDetail,
  TimePicker,
} from "./components";
import { usePermission } from "../../hooks/usePermission";
import type { TimeGranularity } from "./components";
import {
  EOM_OPTIONS, WEEK_OPTIONS, YEAR_OPTIONS,
  METRIC_CARDS_ROW1, METRIC_CARDS_ROW2, STATUS_CONFIG,
  OUTPUT_HR_STAGES, FPY_STAGES, MACHINE_DT_STAGES,
  SUMMARY_CARD_CONFIGS,
} from "./constants";
import type { MetricCardType } from "./constants";

// Calculate previous week as default
const _getPrevWeekInfo = (): { year: number; week: number } => {
  const now = new Date();
  const lastWeekDate = new Date(now);
  lastWeekDate.setDate(now.getDate() - 7);
  const d = new Date(Date.UTC(lastWeekDate.getFullYear(), lastWeekDate.getMonth(), lastWeekDate.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
};
const _prevWeekInfo = _getPrevWeekInfo();
const DEFAULT_WEEK = { label: `W${String(_prevWeekInfo.week).padStart(2, "0")}`, value: `W${String(_prevWeekInfo.week).padStart(2, "0")}` } as SelectProps.Option;

const ALL_OPTION: MultiselectProps.Option = { label: "All", value: "__all__" };

const getPlaceholder = (selected: readonly MultiselectProps.Option[]): string => {
  const items = selected.filter((o) => o.value !== "__all__");
  if (items.length === 0) return "All";
  if (items.length === 1) return items[0].label || "";
  return `${items[0].label} +${items.length - 1}`;
};

const handleAllToggle = (
  selected: readonly MultiselectProps.Option[],
  allOptions: MultiselectProps.Options,
  prev: readonly MultiselectProps.Option[],
): readonly MultiselectProps.Option[] => {
  const hadAll = prev.some((o) => o.value === "__all__");
  const nowHasAll = selected.some((o) => o.value === "__all__");
  const itemsOnly = allOptions.filter((o) => o.value !== "__all__");
  if (nowHasAll && !hadAll) {
    return [...itemsOnly, ALL_OPTION];
  }
  if (hadAll && !nowHasAll) {
    return [];
  }
  const withoutAll = selected.filter((o) => o.value !== "__all__");
  if (withoutAll.length === itemsOnly.length) {
    return [...withoutAll, ALL_OPTION];
  }
  return withoutAll;
};

const FilterMultiselect: React.FC<{
  label: string;
  selected: readonly MultiselectProps.Option[];
  options: MultiselectProps.Options;
  onChange: (selected: readonly MultiselectProps.Option[]) => void;
  filteringType?: "auto";
}> = ({ label, selected, options, onChange, filteringType }) => {
  const count = selected.filter((o) => o.value !== "__all__").length;
  return (
    <div data-testid={`program-filter-${label.toLowerCase()}`}>
      <FormField label={count > 0 ? `${label} (${count})` : label}>
        <div className={count > 0 ? "filter-selected" : ""}>
          <Multiselect
            selectedOptions={selected}
            onChange={({ detail }) => onChange(handleAllToggle(detail.selectedOptions, options, selected))}
            options={options}
            placeholder={getPlaceholder(selected)}
            hideTokens
            filteringType={filteringType}
          />
        </div>
      </FormField>
    </div>
  );
};

const DEFAULT_YEAR = YEAR_OPTIONS.find((o) => o.value === String(_prevWeekInfo.year)) || YEAR_OPTIONS.find((o) => o.value === String(new Date().getFullYear())) || YEAR_OPTIONS[2];


const getOverallStatus = (metricsData: ProgramMetricsData, cardId: MetricCardType): string | undefined => {
  switch (cardId) {
    case "otdc": return metricsData.otdc.overall_status;
    case "output_hit_rate": return metricsData.output_hit_rate.overall_status;
    case "machine_downtime": return metricsData.machine_downtime.overall_status;
    case "line_utilization": return metricsData.line_utilization.overall_status;
    case "fpy": return metricsData.fpy.overall_status;
    default: return undefined;
  }
};

interface ProductionTabProps {
  program?: string;
  cm?: string;
  operation?: string;
  location?: string;
  date?: string;
}

const ProductionTab: React.FC<ProductionTabProps> = ({ program: propProgram, cm: propCm, operation: propOperation, location: propLocation, date: propDate }) => {
  const dispatch = useDispatch();
  const { hasPermission, hasDataPermission } = usePermission();
  const initialUrlParams = useRef({
    program: propProgram || null,
    cm: propCm || null,
    location: propLocation || null,
    date: propDate || null,
  });
  const { data: onboardData, status: onboardStatus } = useSelector(
    (state: RootState) => state.program.onboard
  );
  const { data: metricsData, status: metricsStatus } = useSelector(
    (state: RootState) => state.program.metrics
  );
  const { data: detailData, status: detailStatus, cardType: detailCardType } = useSelector(
    (state: RootState) => state.program.detail
  );

  // Filter States
  const [selectedOps, setSelectedOps] = useState<readonly MultiselectProps.Option[]>([]);
  const [selectedCMs, setSelectedCMs] = useState<readonly MultiselectProps.Option[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<readonly MultiselectProps.Option[]>([]);
  const [selectedEOM, setSelectedEOM] = useState<readonly MultiselectProps.Option[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<readonly MultiselectProps.Option[]>([]);
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>("week");
  const [selectedYear, setSelectedYear] = useState<SelectProps.Option | null>(DEFAULT_YEAR);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeek, setSelectedWeek] = useState<SelectProps.Option | null>(DEFAULT_WEEK);
  const [selectedMonth, setSelectedMonth] = useState<SelectProps.Option | null>(null);

  // Filter options derived from onboard data
  const [opOptions, setOpOptions] = useState<MultiselectProps.Options>([]);
  const [cmOptions, setCmOptions] = useState<MultiselectProps.Options>([]);
  const [locOptions, setLocOptions] = useState<MultiselectProps.Options>([]);
  const [progOptions, setProgOptions] = useState<MultiselectProps.Options>([]);

  const [hasSearched, setHasSearched] = useState(false);
  const [searchedPrograms, setSearchedPrograms] = useState<string[]>([]);
  const [searchedCMs, setSearchedCMs] = useState<string[]>([]);
  const [searchedLocations, setSearchedLocations] = useState<string[]>([]);
  const [searchedOps, setSearchedOps] = useState<string[]>([]);
  const [searchedEOM, setSearchedEOM] = useState<string[]>([]);

  // Error flash notifications
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { error: onboardError } = useSelector((state: RootState) => state.program.onboard);
  const { error: metricsError } = useSelector((state: RootState) => state.program.metrics);
  const { error: detailError } = useSelector((state: RootState) => state.program.detail);

  const prevOnboardStatus = useRef(onboardStatus);
  const prevMetricsStatus = useRef(metricsStatus);
  const prevDetailStatus = useRef(detailStatus);

  const showErrorFlash = useCallback((message: string) => {
    const id = String(Date.now());
    setFlashItems((prev) => [
      ...prev,
      { type: "error", content: message, dismissible: true, dismissLabel: "Dismiss", id, onDismiss: () => setFlashItems((items) => items.filter((i) => i.id !== id)) },
    ]);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashItems([]), 8000);
  }, []);

  useEffect(() => {
    if (prevOnboardStatus.current === "loading" && onboardStatus === "failed" && onboardError) {
      showErrorFlash(onboardError);
    }
    prevOnboardStatus.current = onboardStatus;
  }, [onboardStatus]);

  useEffect(() => {
    if (prevMetricsStatus.current === "loading" && metricsStatus === "failed" && metricsError) {
      showErrorFlash(metricsError);
    }
    prevMetricsStatus.current = metricsStatus;
  }, [metricsStatus]);

  useEffect(() => {
    if (prevDetailStatus.current === "loading" && detailStatus === "failed" && detailError) {
      showErrorFlash(detailError);
    }
    prevDetailStatus.current = detailStatus;
  }, [detailStatus]);

  const dispatchMetricsRequest = (programs: string[], cms: string[], locations: string[], ops: string[], eom: string[], overrideTime?: { granularity: TimeGranularity; value: string }) => {
    dispatch(getProgramMetricsRequest({
      programs,
      cm: cms,
      location: locations,
      operation: ops,
      eom,
      time_granularity: overrideTime?.granularity || timeGranularity,
      time_value: overrideTime?.value || getTimeValue(),
    }));
  };
  useEffect(() => {
    const scrollContainer = document.getElementById("app-main-scroll-container");
    if (scrollContainer) scrollContainer.scrollTop = 0;
    dispatch(getProgramOnboardRequest());
  }, [dispatch]);

  // Unified handler: respond to onboard API completion
  useEffect(() => {
    if (onboardStatus === "failed") {
      setHasSearched(false);
      return;
    }
    if (onboardStatus !== "succeeded") return; // idle,loading

    // Succeeded but no data
    if (!onboardData?.programs?.length) {
      setHasSearched(false);
      return;
    }

    // Succeeded with data — derive options (data filtering handled by backend)
    const programs = onboardData.programs;
    if (programs.length === 0) {
      setHasSearched(false);
      return;
    }
    const ops = Array.from(new Set(programs.map((p: ProgramOnboardItem) => p.operation))).sort();
    const cms = Array.from(new Set(programs.map((p: ProgramOnboardItem) => p.cm))).sort();
    const locs = Array.from(new Set(programs.map((p: ProgramOnboardItem) => p.cm_location))).sort();
    const progs = Array.from(new Set(programs.map((p: ProgramOnboardItem) => p.program_name))).sort();

    const newOpOptions = [ALL_OPTION, ...ops.map((v: string) => ({ label: v, value: v }))] as MultiselectProps.Options;
    const newCmOptions = [ALL_OPTION, ...cms.map((v: string) => ({ label: v, value: v }))] as MultiselectProps.Options;
    const newLocOptions = [ALL_OPTION, ...locs.map((v: string) => ({ label: v, value: v }))] as MultiselectProps.Options;
    const newProgOptions = [ALL_OPTION, ...progs.map((v: string) => ({ label: v, value: v }))] as MultiselectProps.Options;
    setOpOptions(newOpOptions);
    setCmOptions(newCmOptions);
    setLocOptions(newLocOptions);
    setProgOptions(newProgOptions);

    // Use URL params captured at mount (stable across re-renders)
    const { program: urlProgram, cm: urlCm, location: urlLocation, date: urlDate } = initialUrlParams.current;

    const firstProgram = programs[0];
    const defaultProg: MultiselectProps.Option = urlProgram
      ? { label: urlProgram, value: urlProgram }
      : { label: firstProgram.program_name, value: firstProgram.program_name };
    const defaultCm: MultiselectProps.Option = urlCm
      ? { label: urlCm, value: urlCm }
      : { label: firstProgram.cm, value: firstProgram.cm };
    const defaultLoc: MultiselectProps.Option = urlLocation
      ? { label: urlLocation, value: urlLocation }
      : { label: firstProgram.cm_location, value: firstProgram.cm_location };

    if (urlDate) {
      setTimeGranularity("day");
      setSelectedDate(urlDate);
    }

    setSelectedCMs([defaultCm]);
    setSelectedLocations([defaultLoc]);
    setSelectedPrograms([defaultProg]);

    const defaultOps = getSelectedOrAll(selectedOps, newOpOptions);
    const defaultEom = getSelectedOrAll(selectedEOM, EOM_OPTIONS);
    setSearchedPrograms([defaultProg.value!]);
    setSearchedCMs([defaultCm.value!]);
    setSearchedLocations([defaultLoc.value!]);
    setSearchedOps(defaultOps);
    setSearchedEOM(defaultEom);

    // Dispatch initial metrics request
    dispatchMetricsRequest(
      [defaultProg.value!], [defaultCm.value!], [defaultLoc.value!], defaultOps, defaultEom,
      urlDate ? { granularity: "day", value: urlDate } : undefined
    );
    setPendingDetailCard("otdc");
    setHasSearched(true);

  }, [onboardStatus]);

  // Active metric card
  const [activeCard, setActiveCard] = useState<MetricCardType | null>("otdc");
  const [pendingDetailCard, setPendingDetailCard] = useState<MetricCardType | null>(null);

  // Dispatch detail request only after metrics succeeds
  useEffect(() => {
    if (metricsStatus === "succeeded" && pendingDetailCard) {
      dispatchDetailRequest(pendingDetailCard);
      setPendingDetailCard(null);
    }
    if (metricsStatus === "failed") {
      setPendingDetailCard(null);
    }
  }, [metricsStatus]);

  const getTimeValue = (): string => {
    const year = selectedYear?.value || String(new Date().getFullYear());
    if (timeGranularity === "week" && selectedWeek?.value) {
      return `${year}-${selectedWeek.value}`;
    }
    if (timeGranularity === "month" && selectedMonth?.value) {
      return `${year}-${selectedMonth.value}`;
    }
    if (timeGranularity === "day" && selectedDate) {
      return selectedDate;
    }
    return `${year}-W01`;
  };

  const getSelectedOrAll = (selected: readonly MultiselectProps.Option[], allOptions: MultiselectProps.Options): string[] => {
    const values = selected.filter((o) => o.value !== "__all__").map((o) => o.value!);
    return values.length > 0 ? values : (allOptions as MultiselectProps.Option[]).filter((o) => o.value !== "__all__").map((o) => o.value!);
  };



  const isSearchDisabled = selectedPrograms.length === 0 || (timeGranularity === "week" && !selectedWeek?.value) || (timeGranularity === "month" && !selectedMonth?.value) || (timeGranularity === "day" && !selectedDate);

  const handleSearch = () => {
    const programs = getSelectedOrAll(selectedPrograms, progOptions);
    const cms = getSelectedOrAll(selectedCMs, cmOptions);
    const locations = getSelectedOrAll(selectedLocations, locOptions);
    const ops = getSelectedOrAll(selectedOps, opOptions);
    const eom = getSelectedOrAll(selectedEOM, EOM_OPTIONS);
    setSearchedPrograms(programs);
    setSearchedCMs(cms);
    setSearchedLocations(locations);
    setSearchedOps(ops);
    setSearchedEOM(eom);
    setExpandedPrograms({});
    dispatchMetricsRequest(programs, cms, locations, ops, eom);
    setHasSearched(true);
    setActiveCard("otdc");
    setPendingDetailCard("otdc");
  };

  const [expandedPrograms, setExpandedPrograms] = useState<Record<string, boolean>>({});
  const toggleProgramExpand = (cardId: string, program: string) => {
    const key = `${cardId}__${program}`;
    setExpandedPrograms((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const isProgramExpanded = (cardId: string, program: string) => !!expandedPrograms[`${cardId}__${program}`];

  const dispatchDetailRequest = (cardId: MetricCardType, programs?: string[], cms?: string[], locations?: string[], ops?: string[], eom?: string[]) => {
    dispatch(getProgramDetailRequest({
      card_type: cardId,
      programs: programs || searchedPrograms,
      cm: cms || searchedCMs,
      location: locations || searchedLocations,
      operation: ops || searchedOps,
      eom: eom || searchedEOM,
      time_granularity: timeGranularity,
      time_value: getTimeValue(),
    }));
  };

  const handleCardClick = (cardId: MetricCardType) => {
    if (activeCard === cardId) {
      setActiveCard(null);
    } else {
      setActiveCard(cardId);
      dispatchDetailRequest(cardId);
    }
  };

  const isSingleMode = !!(metricsData && searchedPrograms.length === 1 && searchedCMs.length <= 1 && searchedLocations.length <= 1);

  const noDataPlaceholder = (
    <div className="card-empty-state">
      <div className="card-empty-state-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18" />
          <path d="M7 16l4-4 3 3 4-4" />
        </svg>
      </div>
      <span className="card-empty-state-text">No data available</span>
      {/* <span className="card-empty-state-hint">Try adjusting filters or time range</span> */}
    </div>
  );

  const isCardEmpty = (cardId: MetricCardType): boolean => {
    if (!metricsData) return true;
    switch (cardId) {
      case "otdc": return metricsData.otdc.programs.length === 0;
      case "output_hit_rate": return metricsData.output_hit_rate.programs.length === 0;
      case "machine_downtime": return metricsData.machine_downtime.programs.length === 0;
      case "line_utilization": return metricsData.line_utilization.programs.length === 0;
      case "fpy": return metricsData.fpy.programs.length === 0;
      case "phic_alert_status": return metricsData.phic.cause_type_summary.length === 0;
      default: return true;
    }
  };


  const renderCardBody = (cardId: MetricCardType) => {
    if (!metricsData || isCardEmpty(cardId)) return noDataPlaceholder;

    if (cardId === "phic_alert_status") {
      return <PhicStatusCard causeTypeSummary={metricsData.phic.cause_type_summary} />;
    }

    if (isSingleMode) {
      if (cardId === "output_hit_rate") {
        return <SingleStageCard programs={metricsData.output_hit_rate.programs} stageConfigs={OUTPUT_HR_STAGES} warningOffset={5} />;
      }
      if (cardId === "machine_downtime") {
        return <SingleStageCard programs={metricsData.machine_downtime.programs} stageConfigs={MACHINE_DT_STAGES} warningOffset={1} invert />;
      }
      if (cardId === "fpy") {
        return <SingleStageCard programs={metricsData.fpy.programs} stageConfigs={FPY_STAGES} warningOffset={2} />;
      }
      const summaryConfig = SUMMARY_CARD_CONFIGS[cardId];
      if (summaryConfig) {
        return <SingleSummaryCard config={summaryConfig} programs={(metricsData as any)[cardId].programs} invertTrend={(cardId as string) === "machine_downtime"} />;
      }
    } else {
      return <MultiModeList cardId={cardId} metricsData={metricsData} isProgramExpanded={isProgramExpanded} toggleProgramExpand={toggleProgramExpand} />;
    }
    return null;
  };

  return (
    <div data-testid="program-page">
    {flashItems.length > 0 && <Flashbar items={flashItems} />}
    <SpaceBetween size="l">
      {/* ========== FILTER AREA ========== */}
      <Container>
        <div className="program-filter-grid" data-testid="program-filter-area">
          <FilterMultiselect
            label="Operation"
            selected={selectedOps}
            options={opOptions}
            onChange={setSelectedOps}
          />
          <FilterMultiselect
            label="CM"
            selected={selectedCMs}
            options={cmOptions}
            onChange={setSelectedCMs}
            filteringType="auto"
          />
          <FilterMultiselect
            label="Location"
            selected={selectedLocations}
            options={locOptions}
            onChange={setSelectedLocations}
            filteringType="auto"
          />
          <FilterMultiselect
            label="Program"
            selected={selectedPrograms}
            options={progOptions}
            onChange={setSelectedPrograms}
            filteringType="auto"
          />
          <FilterMultiselect
            label="EOM"
            selected={selectedEOM}
            options={EOM_OPTIONS}
            onChange={setSelectedEOM}
          />
          <div data-testid="program-filter-time">
            <TimePicker
              granularity={timeGranularity}
              onGranularityChange={setTimeGranularity}
              year={selectedYear}
              onYearChange={setSelectedYear}
              week={selectedWeek}
              onWeekChange={setSelectedWeek}
              month={selectedMonth}
              onMonthChange={setSelectedMonth}
              date={selectedDate}
              onDateChange={setSelectedDate}
            />
          </div>
        </div>

        {/* Selected filter summary */}
        {onboardStatus === "loading" ? (
          <div className="program-filter-summary-skeleton">
            <div className="skeleton-bar" style={{ width: "120px", height: "14px" }} />
            <div className="skeleton-bar" style={{ width: "150px", height: "14px" }} />
            <div className="skeleton-bar" style={{ width: "100px", height: "14px" }} />
          </div>
        ) : (selectedCMs.length > 0 || selectedLocations.length > 0 || selectedPrograms.length > 0) && (
          <div className="program-filter-summary" data-testid="program-filter-summary">
            {selectedCMs.length > 0 && <span data-testid="program-filter-summary-cm"><b>CM:</b> {selectedCMs.map((o) => o.label).join(", ")}</span>}
            {selectedLocations.length > 0 && <span data-testid="program-filter-summary-location"><b>Location:</b> {selectedLocations.map((o) => o.label).join(", ")}</span>}
            {selectedPrograms.length > 0 && <span data-testid="program-filter-summary-program"><b>Program:</b> {selectedPrograms.map((o) => o.label).join(", ")}</span>}
          </div>
        )}

        {/* Buttons row */}
        <div className="program-buttons-row">
          <Button variant="primary" onClick={handleSearch} disabled={isSearchDisabled} iconName="search" data-testid="program-btn-search">Search</Button>
        </div>
      </Container>

      {/* ========== SELECTED PROGRAMS BAR ========== */}
      {(onboardStatus === "loading" || metricsStatus === "loading") && searchedPrograms.length === 0 ? (
        <div className="program-bar-skeleton">
          <div className="skeleton-bar" style={{ width: "130px", height: "20px" }} />
          <div className="program-skeleton-tokens">
            <div className="skeleton-bar" style={{ width: "80px", height: "26px", borderRadius: "12px" }} />
            <div className="skeleton-bar" style={{ width: "70px", height: "26px", borderRadius: "12px" }} />
          </div>
        </div>
      ) : searchedPrograms.length > 0 && (
        <div className="program-bar">
          <span className="program-bar-label">
            <Icon name="status-info" variant="link" /> Programs ({searchedPrograms.length}):
          </span>
          <div data-testid="program-applied-tokens">
            <TokenGroup
              items={searchedPrograms.map((p: string) => ({ label: p, dismissLabel: `Remove ${p}` }))}
              onDismiss={({ detail }) => {
                const newPrograms = searchedPrograms.filter((_: string, i: number) => i !== detail.itemIndex);
                setSearchedPrograms(newPrograms);
                setSelectedPrograms(selectedPrograms.filter((sp) => sp.value !== searchedPrograms[detail.itemIndex]));
                if (newPrograms.length === 0) { setHasSearched(false); setActiveCard(null); }
                else { dispatchMetricsRequest(newPrograms, searchedCMs, searchedLocations, getSelectedOrAll(selectedOps, opOptions), getSelectedOrAll(selectedEOM, EOM_OPTIONS)); }
              }}
            />
          </div>
        </div>
      )}

      {/* ========== DATA AREA ========== */}
      {(onboardStatus === "loading" || metricsStatus === "loading" || (!onboardData && !hasSearched)) ? (
        <div className="program-data-skeleton">
          {[METRIC_CARDS_ROW1, METRIC_CARDS_ROW2].map((row, rowIdx) => (
            <div key={rowIdx} style={{ display: "grid", gridTemplateColumns: rowIdx === 0 ? (isSingleMode ? "1fr 1.6fr 1fr 1fr" : "1fr 1.3fr 1.3fr 1fr") : `repeat(${row.length}, 1fr)`, gap: "12px", alignItems: "stretch" }}>
              {row.map((card) => (
                <div key={card.id} className={`metric-card-container metric-card ${rowIdx === 0 ? "metric-card-row1" : ""} program-card-skeleton-inner`}>
                  <div className="program-card-skeleton-header">
                    <div className="skeleton-bar" style={{ width: "120px", height: "32px", borderRadius: "20px" }} />
                  </div>
                  <div className="program-card-skeleton-body">
                    <div className="skeleton-bar" style={{ flex: 1, borderRadius: "6px" }} />
                    <div className="skeleton-bar" style={{ flex: 1, borderRadius: "6px" }} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : !hasSearched ? (
        <Container>
          <Box textAlign="center" padding={{ vertical: "xxxl" }} color="text-body-secondary">
            <SpaceBetween size="s" alignItems="center">
              <Icon name="search" size="big" variant="disabled" />
              <Box variant="h3" color="text-body-secondary"><span data-testid="program-empty-state">Select filters and click Search to view data</span></Box>
              <Box variant="p" color="text-status-inactive">Choose at least one program, then click Search.</Box>
            </SpaceBetween>
          </Box>
        </Container>
      ) : searchedPrograms.length === 0 ? (
        <Container><Box textAlign="center" padding={{ vertical: "xxxl" }}><Box variant="h3"><span data-testid="program-no-programs-state">No programs selected</span></Box></Box></Container>
      ) : (
        <SpaceBetween size="l">
          {/* ===== Metric Cards Grid ===== */}
          <div className="program-cards-column" data-testid="program-cards-grid">
          {[METRIC_CARDS_ROW1, METRIC_CARDS_ROW2].map((row, rowIdx) => (
            <div key={rowIdx} style={{ display: "grid", gridTemplateColumns: rowIdx === 0 ? (isSingleMode ? "1fr 1.6fr 1fr 1fr" : "1fr 1.3fr 1.3fr 1fr") : `repeat(${row.length}, 1fr)`, gap: "12px", alignItems: "stretch" }}>
            {row.map((card) => {
              const isActive = activeCard === card.id;
              const isRow1 = rowIdx === 0;
              const overallStatus = metricsData && isSingleMode ? getOverallStatus(metricsData, card.id) : undefined;
              const statusCfg = overallStatus ? STATUS_CONFIG[overallStatus] : undefined;
              return (
                <div
                  key={card.id}
                  data-testid={`program-card-${card.id}`}
                  data-card-active={String(isActive)}
                  data-card-mode={isSingleMode ? "single" : "multi"}
                  data-status={isSingleMode && statusCfg ? overallStatus : undefined}
                  className={`metric-card-container metric-card ${isActive ? "metric-card-active" : ""} ${isRow1 ? "metric-card-row1" : ""} ${isSingleMode || isCardEmpty(card.id) ? "metric-card-fixed" : ""}`}
                  onClick={() => handleCardClick(card.id)}
                  style={{
                    "--card-color": card.color,
                    "--card-bg": `${card.color}12`,
                    "--card-shadow": `${card.color}55`,
                  } as React.CSSProperties}
                >
                  <div className="metric-card-header">
                    <span className="card-title-badge">
                      <span className="card-title-icon" style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)` }}>
                        {card.title.charAt(0)}
                      </span>
                      {card.title}
                    </span>
                    {statusCfg && (
                      <span className="metric-card-status-badge" style={{ color: statusCfg.color, backgroundColor: statusCfg.bg, borderColor: statusCfg.border }}>
                        {statusCfg.label}
                      </span>
                    )}
                  </div>
                  {renderCardBody(card.id)}
                </div>
              );
            })}
            </div>
          ))}
          </div>
          {/* ===== Detail Panel (shown when a card is clicked) ===== */}
          {activeCard === "output_hit_rate" && (
            <ProductionOnlyDetail
              detailData={detailCardType === "output_hit_rate" ? detailData : null}
              detailStatus={detailCardType === "output_hit_rate" ? detailStatus : "idle"}
              timeGranularity={timeGranularity}
            />
          )}
          {activeCard === "fpy" && (
            <TestingFPYDetail
              programs={searchedPrograms}
              timeGranularity={timeGranularity}
              timeValue={timeGranularity === "week" ? selectedWeek?.value || null : timeGranularity === "month" ? selectedMonth?.value || null : null}
              selectedCMs={searchedCMs}
              selectedLocations={searchedLocations}
              detailData={detailCardType === "fpy" ? detailData : null}
              detailStatus={detailCardType === "fpy" ? detailStatus : "idle"}
            />
          )}
          {activeCard === "machine_downtime" && (
            <MachineDowntimeDetail
              detailData={detailCardType === "machine_downtime" ? detailData : null}
              detailStatus={detailCardType === "machine_downtime" ? detailStatus : "idle"}
              timeGranularity={timeGranularity}
            />
          )}
          {activeCard === "otdc" && (
            <ShipmentOnlyDetail
              detailData={detailCardType === "otdc" ? detailData : null}
              detailStatus={detailCardType === "otdc" ? detailStatus : "idle"}
              timeGranularity={timeGranularity}
            />
          )}
          {activeCard === "line_utilization" && (
            <LineUtilizationDetail
              detailData={detailCardType === "line_utilization" ? detailData : null}
              detailStatus={detailCardType === "line_utilization" ? detailStatus : "idle"}
            />
          )}
          {activeCard === "phic_alert_status" && (
            <PHICStatusDetail
              programs={searchedPrograms}
              timeGranularity={timeGranularity}
              timeValue={getTimeValue()}
              selectedCMs={searchedCMs}
              selectedLocations={searchedLocations}
              detailData={detailCardType === "phic_alert_status" ? detailData : null}
              detailStatus={detailCardType === "phic_alert_status" ? detailStatus : "idle"}
            />
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
    </div>
  );
};

const Program: React.FC<ProductionTabProps> = (props) => {
  // const { hasPermission } = usePermission();

  // const tabs = [];
  // if (hasPermission("program.p_view_production")) {
  //   tabs.push({ id: "production", label: "Program Performance", content: <div data-testid="program-tab-production-content"><ProductionTab /></div> });
  // }

  return (
    <ProductionTab {...props} />
    // <div data-testid="program-tabs" className="program-page-container">
    //   <Tabs tabs={tabs} />
    // </div>
  );
};

export default Program;
